import React from 'react';
import styled from 'styled-components';
import { useModal } from 'components/shared/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Spinner, Billboard } from '@companycam/slab-web';
import { translate } from 'javascripts/translations/i18n';

import CCModal from 'components/shared/DesignSystem/CCModal/CCModal';
import ProjectPage from './ProjectPage';
import ProjectPageLegacy from './ProjectPageLegacy';
import { LD_DOCUMENTS_DOCUMENT_BUILDER } from 'constants/featureFlags';
import { useGetProjectPage } from 'data/v3';
import { PageObject } from 'data/v3/data-contracts';

type StyledModalProps = {
  isOpen: boolean;
  ariaHideApp: boolean;
  contentLabel: string;
  overlayClassName: string;
};

const StyledModal = styled(CCModal)<StyledModalProps>`
  max-width: 960px;
  width: 90%;
  height: 90vh;
`;

const LoadingContainer = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
`;

const ModalBody = ({
  isLoading,
  error,
  page,
  projectDbId,
  documentBuilderEnabled,
  hideModal,
}: {
  isLoading: boolean;
  error: boolean;
  page: PageObject;
  projectDbId: string;
  documentBuilderEnabled: boolean;
  hideModal: () => void;
}) => {
  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner
          accessibilityLabel={translate('common.loading')}
          size="large"
          layout="vertical"
        />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <Billboard
        color="destroy"
        iconName="alert-circle-outline"
        message={translate('projectPage.getPageError')}
        title={translate('common.error')}
      />
    );
  }

  return documentBuilderEnabled ? (
    <ProjectPage
      page={page}
      projectDbId={Number(projectDbId)}
      onDelete={hideModal}
    />
  ) : (
    <ProjectPageLegacy
      page={page}
      projectDbId={Number(projectDbId)}
      onDelete={hideModal}
    />
  );
};

// TODO: Delete this file once project page is moved out of modal
const ProjectPageModal = ({
  projectDbId,
  pageDbId,
}: {
  projectDbId: string;
  pageDbId: string;
}) => {
  const { hideModal } = useModal();
  const { [LD_DOCUMENTS_DOCUMENT_BUILDER]: documentBuilderEnabled } =
    useFlags();

  const { page, isLoading, error } = useGetProjectPage({
    pageDbId: Number(pageDbId),
    projectDbId: Number(projectDbId),
  });

  return (
    <StyledModal
      isOpen
      testId="project-page-modal"
      ariaHideApp={false}
      contentLabel="Project Page"
      overlayClassName="modal"
      onRequestClose={hideModal}
      shouldCloseOnOverlayClick
      showCloseButton={false}
      bodyStyle={{
        margin: 0,
        padding: 0,
        borderTopRightRadius: 12,
        borderTopLeftRadius: 12,
      }}
    >
      <ModalBody
        isLoading={isLoading}
        page={page}
        error={!!error}
        projectDbId={projectDbId}
        documentBuilderEnabled={documentBuilderEnabled}
        hideModal={hideModal}
      />
    </StyledModal>
  );
};

export default ProjectPageModal;
